import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  parseImportFile, confirmImport,
  ImportPreviewResult, ImportSheetCandidate,
} from './api';
import './recipes.css';
import DataTable from '../../shared/components/DataTable';

type Step = 'upload' | 'preview' | 'done';

const RecipeImport: React.FC = () => {
  const { sessionToken } = useAuth();

  const [step, setStep] = useState<Step>('upload');
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState<{ created: number; skipped: number; errors: any[] } | null>(null);

  // Upload + parse
  const handleFile = (file: File) => {
    setError('');
    if (!file.name.match(/\.(xlsx|xls|xlsm)$/i)) {
      setError('Please upload an Excel file (.xlsx, .xls, .xlsm)');
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = (e.target?.result as string).split(',')[1];
      if (!base64) { setError('Could not read file'); return; }
      setLoading(true);
      try {
        const result = await parseImportFile(sessionToken, base64, file.name);
        setPreview(result);
        // Pre-select all candidates by default
        const allNames = result.sheets.flatMap(s => s.candidate_recipes.map(r => r.name));
        setSelected(new Set(allNames));
        setStep('preview');
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const toggleRecipe = (name: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleAll = (names: string[], check: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      names.forEach(n => check ? next.add(n) : next.delete(n));
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setLoading(true);
    setError('');
    try {
      const res = await confirmImport(sessionToken, preview.job.id, Array.from(selected));
      setSummary(res.summary);
      setStep('done');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const allNames = preview?.sheets.flatMap(s => s.candidate_recipes.map(r => r.name)) || [];
  const hasIssues = (c: ImportSheetCandidate) =>
    c.ingredients.some(i => i.issues.length > 0);
  const countFlagged = (c: ImportSheetCandidate) =>
    c.ingredients.filter(i => i.issues.length > 0).length;

  return (
    <div className="rcp-page">
      <Link to="/recipes" className="rcp-back-link">← Back to Recipes</Link>
      <div className="rcp-header">
        <h1>📥 Import Recipes from Excel</h1>
      </div>

      {error && <div className="rcp-error">{error}</div>}

      {/* ── STEP 1: Upload ── */}
      {step === 'upload' && (
        <div className="rcp-section">
          <h2>Step 1: Upload Excel Workbook</h2>
          <p style={{ color: '#64748b', marginBottom: '1.25rem' }}>
            Upload your recipe workbook (.xlsx). The importer will detect sheets, extract recipe names,
            ingredients, and steps, then show you a preview before saving anything.
          </p>

          <div
            className="rcp-import-upload-area"
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => document.getElementById('rcp-file-input')?.click()}
          >
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📊</div>
            <strong>Drop your Excel file here</strong>
            <p>or click to browse · Supports .xlsx, .xls, .xlsm</p>
            <input
              id="rcp-file-input"
              type="file"
              accept=".xlsx,.xls,.xlsm"
              className="rcp-import-hidden-input"
              onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
            />
          </div>

          {loading && <div className="rcp-loading" style={{ marginTop: '1rem' }}>Parsing workbook...</div>}

          <div style={{ marginTop: '1.5rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>What the importer detects:</h4>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.85rem', color: '#64748b', lineHeight: 1.7 }}>
              <li>Each sheet → recipe category</li>
              <li>Single-cell rows that look like names → candidate recipe names</li>
              <li>2–4 column rows with quantity + ingredient → ingredient lines</li>
              <li>Long text rows starting with action words → steps</li>
              <li>Fractions ("1/2 cup"), free-text ("to taste"), vague units ("1 bunch") are flagged for review</li>
            </ul>
          </div>
        </div>
      )}

      {/* ── STEP 2: Preview ── */}
      {step === 'preview' && preview && (
        <>
          <div className="rcp-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h2 style={{ margin: 0 }}>Step 2: Review & Select</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="rcp-btn rcp-btn-sm" onClick={() => toggleAll(allNames, true)}>Select All</button>
                <button className="rcp-btn rcp-btn-sm" onClick={() => toggleAll(allNames, false)}>Deselect All</button>
              </div>
            </div>
            <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.875rem' }}>
              Found <strong>{preview.total_candidate_recipes}</strong> candidate recipes across{' '}
              <strong>{preview.sheet_count}</strong> sheet{preview.sheet_count !== 1 ? 's' : ''}.
              Select which ones to import as Draft recipes.
            </p>

            <div className="rcp-sheets-list">
              {preview.sheets.map(sheet => (
                <div key={sheet.sheet_name} className="rcp-sheet-card">
                  <div className="rcp-sheet-header">
                    <h3>📂 {sheet.sheet_name}</h3>
                    <span className="rcp-text-muted">{sheet.candidate_recipes.length} recipes</span>
                  </div>

                  {sheet.candidate_recipes.length === 0 ? (
                    <div className="rcp-empty" style={{ padding: '0.75rem 1rem' }}>No candidate recipes detected in this sheet</div>
                  ) : (
                    sheet.candidate_recipes.map(candidate => (
                      <div key={candidate.name} className="rcp-recipe-preview">
                        <h4>
                          <input
                            type="checkbox"
                            checked={selected.has(candidate.name)}
                            onChange={() => toggleRecipe(candidate.name)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          {candidate.name}
                          {hasIssues(candidate) && (
                            <span className="rcp-tag rcp-tag-review" style={{ fontSize: '0.68rem', marginLeft: '0.5rem' }}>
                              ⚠️ {countFlagged(candidate)} flag{countFlagged(candidate) !== 1 ? 's' : ''}
                            </span>
                          )}
                        </h4>

                        {candidate.ingredients.length > 0 && (
                          <div style={{ marginLeft: '1.5rem' }}>
                            <DataTable
                              rows={candidate.ingredients}
                              rowKey={(_ing, ii) => ii}
                              caption="Parsed ingredients"
                              empty="No ingredients parsed."
                              columns={[
                                { key: 'name', header: 'Ingredient', priority: 'primary', render: (ing) => ing.ingredient_name || <em className="rcp-text-danger">—</em> },
                                { key: 'qty', header: 'Quantity', priority: 'summary', render: (ing) => ing.quantity_text || <em className="rcp-text-muted">blank</em> },
                                {
                                  key: 'issues', header: 'Issues', priority: 'summary',
                                  render: (ing) => (ing.issues.length === 0 ? (
                                    <span className="rcp-text-ok">✓</span>
                                  ) : (
                                    <ul className="rcp-issues-list">
                                      {ing.issues.map((iss: string, ji: number) => (
                                        <li key={ji} className={ing.fatal ? 'rcp-issue-fatal' : ''}>{iss}</li>
                                      ))}
                                    </ul>
                                  )),
                                },
                              ]}
                            />
                            {candidate.steps.length > 0 && (
                              <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: '#64748b' }}>
                                {candidate.steps.length} step{candidate.steps.length !== 1 ? 's' : ''} detected
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="rcp-form-actions">
            <button className="rcp-btn" onClick={() => { setStep('upload'); setPreview(null); }}>← Back</button>
            <button
              className="rcp-btn rcp-btn-primary"
              onClick={handleConfirm}
              disabled={loading || selected.size === 0}
            >
              {loading ? 'Importing...' : `Import ${selected.size} Recipe${selected.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </>
      )}

      {/* ── STEP 3: Done ── */}
      {step === 'done' && summary && (
        <div className="rcp-section">
          <h2>✅ Import Complete</h2>
          <div className="rcp-import-summary-grid">
            <div className="rcp-import-summary-card success">
              <div className="rcp-summary-val">{summary.created}</div>
              <div className="rcp-summary-lbl">Recipes Created</div>
            </div>
            <div className="rcp-import-summary-card warning">
              <div className="rcp-summary-val">{summary.skipped}</div>
              <div className="rcp-summary-lbl">Skipped</div>
            </div>
            <div className="rcp-import-summary-card">
              <div className="rcp-summary-val">{summary.errors.length}</div>
              <div className="rcp-summary-lbl">Errors</div>
            </div>
          </div>

          {summary.errors.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
              <strong>Errors:</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', fontSize: '0.85rem' }}>
                {summary.errors.map((err, i) => (
                  <li key={i}><strong>{err.recipe}:</strong> {err.error}</li>
                ))}
              </ul>
            </div>
          )}

          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
            All imported recipes are created as <strong>Draft</strong>. Review and activate each one after
            confirming ingredients are linked to inventory items.
          </p>

          <div className="rcp-form-actions" style={{ justifyContent: 'flex-start' }}>
            <Link to="/recipes" className="rcp-btn rcp-btn-primary">View All Recipes</Link>
            <button className="rcp-btn" onClick={() => { setStep('upload'); setPreview(null); setSummary(null); }}>
              Import More
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeImport;
